// @ts-check

/**
 * @file Provides the project board column action menu.
 */
import PropTypes from "prop-types"
import PropTypesExact from "prop-types-exact"
import _ from "gettext-universal/build/src/translate.js"
import useLocale from "gettext-universal/build/src/use-locale-expo.js"
import {memo} from "react"
import {ShapeComponent, shapeComponent} from "set-state-compare/build/shape-component.js"
import ActionMenu from "../../../components/action-menu"

/** @import {NamedExoticComponent, ReactNode} from "react" */

/**
 * @typedef {object} ProjectBoardColumnMenuProps
 * @property {boolean} isVisible Whether the menu is visible.
 * @property {(...args: unknown[]) => unknown} onAddTaskPress Add-task press handler.
 * @property {(...args: unknown[]) => unknown} onClosePress Close press handler.
 * @property {(...args: unknown[]) => unknown} onDeletePress Delete press handler.
 * @property {(...args: unknown[]) => unknown} onEditPress Edit press handler.
 */

/** @typedef {Record<string, never>} ProjectBoardColumnMenuState */

/**
 * Project board column action menu.
 * @augments {ShapeComponent<ProjectBoardColumnMenuProps, ProjectBoardColumnMenuState>}
 */
class ProjectBoardColumnMenu extends ShapeComponent {
  static propTypes = PropTypesExact({
    isVisible: PropTypes.bool.isRequired,
    onAddTaskPress: PropTypes.func.isRequired,
    onClosePress: PropTypes.func.isRequired,
    onDeletePress: PropTypes.func.isRequired,
    onEditPress: PropTypes.func.isRequired
  })

  /** Initializes locale hooks for translated labels. */
  setup() {
    useLocale()
  }

  /**
   * Renders the column action menu.
   * @returns {ReactNode} Column action menu content.
   */
  render() {
    return (
      <ActionMenu
        isVisible={this.p.isVisible}
        items={[
          {
            iconName: "plus",
            label: _("Add task"),
            onPress: this.p.onAddTaskPress,
            testID: "projectBoardColumnMenu/addTaskButton"
          },
          {
            iconName: "pen-to-square",
            label: _("Edit Column"),
            onPress: this.p.onEditPress,
            testID: "projectBoardColumnMenu/editButton"
          },
          {
            destructive: true,
            iconName: "trash",
            label: _("Delete Column"),
            onPress: this.p.onDeletePress,
            testID: "projectBoardColumnMenu/deleteButton"
          }
        ]}
        onClosePress={this.p.onClosePress}
        testID="projectBoardColumnMenu"
        title={_("Column actions")}
      />
    )
  }
}

/** @type {NamedExoticComponent<ProjectBoardColumnMenuProps>} */
const ProjectBoardColumnMenuComponent = memo(shapeComponent(ProjectBoardColumnMenu))

export default ProjectBoardColumnMenuComponent
